import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../../Components/Request';
import { useAuth } from '../../../Components/AuthContext';
import hasPermission from '../../../Hooks/hasPermission';
import { canWriteCatalog } from '../../lib/catalogPermissions';

export interface DeliveryFormState {
  client_id: string;
  name: string;
  scac: string;
  role: string;
  phone: string;
  email: string;
  contact_main: string;
  contact_warehouse: string;
  commodity_id: string;
  shedules_start: string;
  shedules_end: string;
  stackable: boolean;
  redidential_place: boolean;
  lift_dolly: boolean;
  open_daily: boolean;
  mandatory: boolean;
  beside_pod: boolean;
  street_one: string;
  street_two: string;
  local_number: string;
  colony: string;
  zipcode: string;
  city: string;
  state: string;
  latitude: string;
  longitude: string;
}

const empty: DeliveryFormState = {
  client_id: '',
  name: '',
  scac: '',
  role: '',
  phone: '',
  email: '',
  contact_main: '',
  contact_warehouse: '',
  commodity_id: '',
  shedules_start: '',
  shedules_end: '',
  stackable: false,
  redidential_place: false,
  lift_dolly: false,
  open_daily: false,
  mandatory: false,
  beside_pod: false,
  street_one: '',
  street_two: '',
  local_number: '',
  colony: '',
  zipcode: '',
  city: '',
  state: '',
  latitude: '',
  longitude: '',
};

const toTime = (v: any): string => {
  if (!v) return '';
  if (typeof v === 'string' && /^\d{1,2}:\d{2}/.test(v)) return v.slice(0, 5);
  const d = dayjs(v);
  return d.isValid() ? d.format('HH:mm') : '';
};

export const useDeliveryForm = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  useEffect(() => {
    navRef.current = navigate;
  }, [navigate]);

  const { user: authUser } = useAuth();
  const perms = useMemo(() => {
    const p = authUser?.permissionsActives;
    return {
      canViewClients: hasPermission(p, 'CL_VIEW'),
      canEditClients: hasPermission(p, 'CL_EDIT'),
      canCreate: hasPermission(p, 'DL_CREATE'),
      canEdit: hasPermission(p, 'DL_EDIT'),
      /**
       * *Add commodity* solo para quien puede crear en los catálogos (decisión 15, 12-oct). Escribir un nombre que no
       * existe da de alta el tipo de entrega al guardar la bodega, así que es el mismo alta que el de Catálogos y el
       * backend lo rechaza sin el permiso. Ver `lib/catalogPermissions.ts`.
       */
      canAddCommodity: canWriteCatalog(p, 'create'),
    };
  }, [authUser]);

  const [form, setForm] = useState<DeliveryFormState>(empty);
  const setField = useCallback(<K extends keyof DeliveryFormState>(k: K, v: DeliveryFormState[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
  }, []);

  const [clients, setClients] = useState<{ id: string; name: string }[]>([]);
  const [commodities, setCommodities] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const safe = (url: string) => axiosInstance.get(url).then((r) => r.data).catch(() => []);
      const [cl, co] = await Promise.all([
        perms.canViewClients ? safe('/clients') : Promise.resolve([]),
        safe('/deliveries/commoditys'),
      ]);
      setClients(cl);
      setCommodities(co);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perms.canViewClients]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await axiosInstance.get(`/deliveries/${id}`);
        const d = res.data;
        setForm({
          ...empty,
          ...Object.keys(empty).reduce((acc, key) => {
            const k = key as keyof DeliveryFormState;
            const val = (d as any)[k];
            (acc as any)[k] = val == null ? empty[k] : val;
            return acc;
          }, {} as Partial<DeliveryFormState>),
          client_id: d.client_id || d.client?.id || '',
          commodity_id: d.commodity_id || '',
          shedules_start: toTime(d.shedules_start),
          shedules_end: toTime(d.shedules_end),
          latitude: d.latitude != null ? String(d.latitude) : '',
          longitude: d.longitude != null ? String(d.longitude) : '',
        });
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 401) navRef.current('/login');
        else if (status === 404) navRef.current('/deliveries');
        else if (status === 403) navRef.current('/');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const addCommodity = (name: string) => {
    if (!name.trim()) return;
    setCommodities((prev) => [{ id: name.trim(), name: name.trim() }, ...prev]);
    setField('commodity_id', name.trim());
  };

  // Phone y Mail ya NO son obligatorios para crear/guardar un delivery.
  const requiredMissing =
    !form.name.trim() ||
    !form.scac.trim() ||
    !form.role ||
    !form.street_one.trim() ||
    !form.zipcode.trim() ||
    !form.city.trim() ||
    !form.state.trim();

  const buildPayload = () => {
    const p: any = { ...form };
    // Normalizar vacíos a undefined/null donde aplica
    ['client_id', 'commodity_id', 'shedules_start', 'shedules_end'].forEach((k) => {
      if (!p[k]) p[k] = null;
    });
    p.latitude = form.latitude ? Number(form.latitude) : null;
    p.longitude = form.longitude ? Number(form.longitude) : null;
    return p;
  };

  const submit = async () => {
    if (requiredMissing || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        await axiosInstance.put(`/deliveries/${id}`, payload);
        return true;
      } else {
        const res = await axiosInstance.post('/deliveries', payload);
        navRef.current(`/deliveries/${res.data.id}`);
        return true;
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'An error occurred');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/deliveries/${id}`);
      navRef.current('/deliveries');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return {
    isEdit,
    perms,
    form,
    setField,
    clients,
    commodities,
    addCommodity,
    loading,
    submitting,
    deleting,
    error,
    requiredMissing,
    submit,
    remove,
  };
};
